import { Client, type IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import {
  ChatResueltoException,
  SesionDesactualizadaException,
  type HistoryMessage,
  type NativeMediaResponse,
  type NativeMessageResponse,
  type WsTopicEvent,
} from './types';

export type ConnectionState = 'connecting' | 'connected' | 'error' | 'closed';

export interface TransportCallbacks {
  /** Cualquier evento del topic (mensaje normal o evento de ESTADO). */
  onTopicEvent: (event: WsTopicEvent) => void;
  onState: (state: ConnectionState) => void;
}

/** Datos de la sesión activa que el transporte necesita para conectar/enviar. */
export interface TransportSession {
  token: string;
  tenant: string;
  chatId: string;
}

/**
 * Transporte realtime + envío del widget.
 * - STOMP sobre SockJS contra `${wsUrl}/ws`, header Authorization: Bearer <token>.
 * - Suscripción a `/topic/chat.{tenant}.{chatId}` (mismo shape que dungo-front).
 * - Reconexión básica delegada al cliente STOMP (reconnectDelay).
 *
 * La sesión (token/tenant/chatId) se inyecta con `configure()` al iniciar cada
 * conversación, porque con provisión bajo demanda (`getSession`) no se conoce
 * hasta el mint.
 */
export class DungoTransport {
  private client: Client | null = null;
  private session: TransportSession | null = null;

  constructor(
    private readonly opts: { wsUrl: string; reciverBaseUrl: string },
    private readonly cb: TransportCallbacks,
  ) {}

  /** Fija la sesión activa para la próxima conexión/envío/historial. */
  configure(session: TransportSession): void {
    this.session = session;
  }

  private requireSession(): TransportSession {
    if (!this.session) throw new Error('[DungoWidget] no hay sesión activa.');
    return this.session;
  }

  connect(): void {
    const session = this.requireSession();
    this.cb.onState('connecting');
    const topic = `/topic/chat.${session.tenant}.${session.chatId}`;
    const base = this.opts.wsUrl.replace(/\/+$/, '');

    this.client = new Client({
      webSocketFactory: () => new SockJS(`${base}/ws`) as unknown as WebSocket,
      connectHeaders: { Authorization: `Bearer ${session.token}` },
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      reconnectDelay: 5000,
      onConnect: () => {
        this.cb.onState('connected');
        this.client?.subscribe(topic, (message: IMessage) => {
          try {
            this.cb.onTopicEvent(JSON.parse(message.body) as WsTopicEvent);
          } catch {
            /* mensaje no-JSON: se ignora */
          }
        });
      },
      onStompError: () => this.cb.onState('error'),
      onWebSocketError: () => this.cb.onState('error'),
      onWebSocketClose: () => {
        // El cliente reintentará según reconnectDelay; reportamos estado intermedio.
        if (this.client?.active) this.cb.onState('connecting');
        else this.cb.onState('closed');
      },
    });

    this.client.activate();
  }

  /**
   * POST {reciverBaseUrl}/native/messages -> { chatId, conversacionId }.
   *
   * Si el backend responde 409 con body `{ code:"CHAT_RESUELTO", ... }`, el chat
   * fue resuelto: se lanza `ChatResueltoException` (NO se reintenta el envío) y
   * el widget vuelve a idle.
   */
  async send(text: string, seccion?: string | Record<string, unknown>): Promise<NativeMessageResponse> {
    const session = this.requireSession();
    const base = this.opts.reciverBaseUrl.replace(/\/+$/, '');
    const body: Record<string, unknown> = { text };
    if (seccion !== undefined && seccion !== null && seccion !== '') {
      body.seccion = seccion;
    }
    const res = await fetch(`${base}/native/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.token}`,
      },
      body: JSON.stringify(body),
    });
    if (res.status === 409) {
      // Chat resuelto: verificamos el code del body con tolerancia a fallos.
      let code: unknown;
      try {
        code = ((await res.json()) as { code?: unknown }).code;
      } catch {
        /* body no-JSON: igual lo tratamos como resuelto por el 409 */
      }
      if (code === undefined || code === 'CHAT_RESUELTO') {
        throw new ChatResueltoException();
      }
      if (code === 'SESION_DESACTUALIZADA') {
        throw new SesionDesactualizadaException();
      }
      throw new Error(`native/messages respondió 409`);
    }
    if (!res.ok) {
      throw new Error(`native/messages respondió ${res.status}`);
    }
    return (await res.json()) as NativeMessageResponse;
  }

  /**
   * POST {reciverBaseUrl}/native/media (multipart) -> mensaje de imagen persistido.
   *
   * Sube una imagen (`archivo`, solo `image/*`, máx 5 MB) con un `caption`
   * opcional. Mismo manejo de 409 CHAT_RESUELTO que `send()`: lanza
   * `ChatResueltoException` y el widget vuelve a idle sin reintentar.
   *
   * `seccion` es la metainfo del envío (misma que en `/native/messages`). En
   * multipart no hay JSON, así que un objeto se serializa a string JSON en el
   * campo `seccion` del form; un string se manda tal cual.
   */
  async sendMedia(
    file: File,
    caption?: string,
    seccion?: string | Record<string, unknown>,
  ): Promise<NativeMediaResponse> {
    const session = this.requireSession();
    const base = this.opts.reciverBaseUrl.replace(/\/+$/, '');
    const form = new FormData();
    form.append('archivo', file);
    if (caption !== undefined && caption !== null && caption !== '') {
      form.append('caption', caption);
    }
    if (seccion !== undefined && seccion !== null && seccion !== '') {
      form.append('seccion', typeof seccion === 'string' ? seccion : JSON.stringify(seccion));
    }
    const res = await fetch(`${base}/native/media`, {
      method: 'POST',
      // No seteamos Content-Type: el navegador agrega el boundary del multipart.
      headers: { Authorization: `Bearer ${session.token}` },
      body: form,
    });
    if (res.status === 409) {
      let code: unknown;
      try {
        code = ((await res.json()) as { code?: unknown }).code;
      } catch {
        /* body no-JSON: igual lo tratamos como resuelto por el 409 */
      }
      if (code === undefined || code === 'CHAT_RESUELTO') {
        throw new ChatResueltoException();
      }
      if (code === 'SESION_DESACTUALIZADA') {
        throw new SesionDesactualizadaException();
      }
      throw new Error(`native/media respondió 409`);
    }
    if (!res.ok) {
      throw new Error(`native/media respondió ${res.status}`);
    }
    return (await res.json()) as NativeMediaResponse;
  }

  /**
   * GET {reciverBaseUrl}/native/messages?limit=N -> historial en orden ASCENDENTE.
   * Falla con gracia: ante error de red o respuesta no-OK devuelve [] y loguea warn,
   * de modo que el widget siga funcionando para mensajes nuevos.
   */
  async fetchHistory(limit = 50): Promise<HistoryMessage[]> {
    const session = this.requireSession();
    const base = this.opts.reciverBaseUrl.replace(/\/+$/, '');
    try {
      const res = await fetch(`${base}/native/messages?limit=${limit}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${session.token}` },
      });
      if (!res.ok) {
        console.warn(`[DungoWidget] historial respondió ${res.status}; se omite.`);
        return [];
      }
      const data = (await res.json()) as unknown;
      return Array.isArray(data) ? (data as HistoryMessage[]) : [];
    } catch (err) {
      console.warn('[DungoWidget] no se pudo cargar el historial:', err);
      return [];
    }
  }

  disconnect(): void {
    void this.client?.deactivate();
    this.client = null;
    this.cb.onState('closed');
  }
}
